import React, { ChangeEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { AppDispatch, RootState } from '../../reducers/store';
import { Button, FormField, ImageBuffer, Loader, VerifyOtp } from '../../components';
import { PhotoRounded, Visibility, VisibilityOff } from '@mui/icons-material';
import { actionCreateAdmin, actionFetchAdmins } from '../../actions/admins';
import toast from 'react-hot-toast';

import { adminValidateFirstName, adminValidateLastName, adminValidateEmailAddress, adminValidateUsername, adminValidatePassword, adminValidateConfirmPassword, adminValidateSelectedFile } from '../../components/validations/admins/register';

import './accountLogin.css'

interface PostDataParams {
  selectedFile?: File | null;
  firstName: string;
  lastName: string;
  emailAddress: string;
  username: string;
  password: string;
  confirmPassword: string;
}

interface OtpParams{
  _id?: string;
  emailAddress?: string
}

const AccountRegister = () => {
  const pageName                                          = "Register"
  const dispatch                                          = useDispatch<AppDispatch>();
  const [hideShow, setHideShow]                           = useState<boolean>(false);
  const [savingInfo, setSavingInfo]                       = useState<boolean>(false);
  const [fileNames, setFileNames]                         = useState<{ [key: string]: string }>({});
  const [openOtpBox, setOpenOtpBox]                       = useState<boolean>(false);
  const [otpData, setOtpData]                             = useState<OtpParams>();
  const [userImg, setUserImg]                             = useState<string>();
  const [selectedFileErrors, setSelectedFileErrors]       = useState<string[]>([]);
  const [firstNameErrors, setFirstNameErrors]             = useState<string[]>([]);
  const [lastNameErrors, setLastNameErrors]               = useState<string[]>([]);
  const [emailAddressErrors, setEmailAddressErrors]       = useState<string[]>([]);
  const [usernameErrors, setUsernameErrors]               = useState<string[]>([]);
  const [passwordErrors, setPasswordErrors]               = useState<string[]>([]);
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState<string[]>([]);
  const [isButtonDisabled, setIsButtonDisabled]           = useState<boolean>(true);
  const [errorMessage, setErrorMessage]                   = useState<string>();
  const { getAllAdmins, currentPage }                     = useSelector((state: RootState) => state.adminList);

  const [postData, setPostData]                  = useState<PostDataParams>({ selectedFile: null, firstName: '', lastName: '', emailAddress: '', username: '', password: '', confirmPassword: ''})
  const [isLoadingBtn, setIsLoadingBtn]          = useState({ selectedFile: false, firstName: false, lastName: false, emailAddress: false, username: false, password: false, confirmPassword: false});
  const [isValid, setIsValid]                    = useState({ selectedFile: false, firstName: false, lastName: false, emailAddress: false, username: false, password: false, confirmPassword: false});

  const useDebounce = <T,>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);
  
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebounced(value);
      }, delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
  
    return debounced;
  };

  const debouncedPostData = useDebounce<PostDataParams>(postData, 500)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIsLoadingBtn((prevState) => ({ ...prevState, [name]: true }));
    setPostData((prevState) => ({ ...prevState, [name]: value }));
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileNames(prev => ({
      ...prev,
      [e.target.name]: file.name,
    }));

    // Store RAW file
    setPostData(prev => ({
      ...prev,
      selectedFile: file,
    }));

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setUserImg(previewUrl);
  };

  const handleUserImageUpload = (e: any) => {
    const file = e.target.files[0]

    transformFile(file)
  }

  const transformFile = (file: File | null) => {
    const reader = new FileReader();
  
    if (file) {
      reader.readAsDataURL(file);
  
      reader.onloadend = () => {
        const result = reader.result;
  
        // Ensure the result is a string before updating state
        if (typeof result === 'string') {
          setUserImg(result);
        }
      };
    } else {
      setUserImg(''); // Reset the array if no file is passed
    }
  };

  useEffect(() => {
    dispatch(actionFetchAdmins({currentPage})); // You can pass the page number as needed
  }, [dispatch, currentPage]);

  useEffect(() => {
    const validateSelectedFile = () => {
      const { isValid, errors } = adminValidateSelectedFile({ postData: debouncedPostData });
      setIsValid((prevState) => ({
          ...prevState,
          selectedFile: isValid,
      }));
      setSelectedFileErrors(errors);
      setIsLoadingBtn((prevState) => ({
          ...prevState,
          selectedFile: false,
      }));
      return errors;
    };

    const validateFirstName = () => {
      const { isValid, errors } = adminValidateFirstName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        firstName: isValid,
      }));
      setFirstNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        firstName: false,
      }));
      return errors;
    };

    const validateLastName = () => {
      const { isValid, errors } = adminValidateLastName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        lastName: isValid,
      }));
      setLastNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        lastName: false,
      }));
      return errors;
    };
  
    const validateEmailAddress = () => {
      const { isValid, errors } = adminValidateEmailAddress({ postData: debouncedPostData, getAllAdmins });
      setIsValid((prevState) => ({
        ...prevState,
        emailAddress: isValid,
      }));
      setEmailAddressErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        emailAddress: false,
      }));
      return errors;
    };

    const validateUsername = () => {
      const { isValid, errors } = adminValidateUsername({ postData: debouncedPostData, getAllAdmins });
      setIsValid((prevState) => ({
        ...prevState,
        username: isValid,
      }));
      setUsernameErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        username: false,
      }));
      return errors;
    };

    const validatePassword = () => {
      const { isValid, errors } = adminValidatePassword({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        password: isValid,
      }));
      setPasswordErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        password: false,
      }));
      return errors;
    };

    // the confirm password function should connect from the postData since you're comparing two fields
    const validateConfirmPassword = () => {
      const { isValid, errors } = adminValidateConfirmPassword({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        confirmPassword: isValid,
      }));
      setConfirmPasswordErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        confirmPassword: false,
      }));
      return errors;
    };

    const selectedFileErrors     = validateSelectedFile();
    const firstNameErrors        = validateFirstName();
    const lastNameErrors         = validateLastName();
    const emailAddressErrors     = validateEmailAddress();
    const usernameErrors         = validateUsername();
    const passwordErrors         = validatePassword();
    const confirmPasswordErrors  = validateConfirmPassword();
    
    setSelectedFileErrors(selectedFileErrors)
    setFirstNameErrors(firstNameErrors);
    setLastNameErrors(lastNameErrors);
    setEmailAddressErrors(emailAddressErrors);
    setUsernameErrors(usernameErrors);
    setPasswordErrors(passwordErrors);
    setConfirmPasswordErrors(confirmPasswordErrors);

    const hasFormErrors = selectedFileErrors.length > 0 || firstNameErrors.length > 0 || lastNameErrors.length > 0 || emailAddressErrors.length > 0 || usernameErrors.length > 0 || passwordErrors.length > 0 || confirmPasswordErrors.length > 0;
    setIsButtonDisabled(hasFormErrors);
  }, [debouncedPostData]);

  // console.log(otpData)
  const handleSubmit = async(e: any) => {
    e.preventDefault();
    setSavingInfo(true);
    setIsButtonDisabled(true);
    const formData = new FormData();

    Object.entries(postData).forEach(([key, value]) => {
      if (value !== null) {
        formData.append(key, value as any);
      }
    });
    const res = await dispatch(actionCreateAdmin({postData: formData}))
    try{
      if (res.success === true) {
        toast.success("user created successfully");
        setPostData({selectedFile: null, firstName: '', lastName: '', emailAddress: '', username: '', password: '', confirmPassword: ''})
        setUserImg('');
        setSavingInfo(false);
        setOpenOtpBox(true);
        setOtpData(res?.result)
      } else if (res?.status === 500) {
        setErrorMessage(res?.message);
        setSavingInfo(false)
      }
    }catch(error: any){
      setErrorMessage("Internal server Error")
      setSavingInfo(false)
    }
  }

  return (
    <>
      <title>{pageName}</title>
      <div className='account-container'>
        <form className='account-login-container' onSubmit={handleSubmit} autoComplete="off" >
          <div className='account-login-container-title'>
            <h3>Admin Sign Up</h3>
          </div>

          {!openOtpBox ? (
            <div className='account-login-input-btn-pass-container'>
              <div className='reg-image-group'>
                <div onChange={handleUserImageUpload}>
                  <FormField fileInputType name="selectedFile" selectedLabelName="Upload your Photo" accept="image/*" handleChange={handleImageUpload} selectedFileName={fileNames.selectedFile} isLoadingBtn={isLoadingBtn.selectedFile} isValid={isValid.selectedFile} errors={selectedFileErrors || []} />
                </div>
                <div className='reg-image-preview-wrapper'>
                  {userImg ? <ImageBuffer src={userImg} alt='userImg' />  : <span><PhotoRounded /></span>}
                </div>
              </div>  

              <FormField inputType type="text" labelName="First Name" name="firstName" value={postData.firstName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.firstName} isValid={isValid.firstName} errors={firstNameErrors || []} />

              <FormField inputType type="text" labelName="Last Name" name="lastName" value={postData.lastName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.lastName} isValid={isValid.lastName} errors={lastNameErrors || []} />

              <FormField inputType type="text" labelName="Email Address" name="emailAddress" value={postData.emailAddress} handleChange={handleChange} isLoadingBtn={isLoadingBtn.emailAddress} isValid={isValid.emailAddress} errors={emailAddressErrors || []} />

              <FormField inputType type="text" labelName="Username" name="username" value={postData.username} handleChange={handleChange} isLoadingBtn={isLoadingBtn.username} isValid={isValid.username} errors={usernameErrors || []} />

              <div className='account-login-group-wrapper'>
                <FormField inputType type={hideShow ? 'text' : 'password'} labelName="Password" name="password" value={postData.password} handleChange={handleChange} isLoadingBtn={isLoadingBtn.password} isValid={isValid.password} errors={passwordErrors || []} />
                <div className='account-login-show-hide-pass-container'>
                <span className={ postData.password.length > 0 ? 'show-hide-password unlock' : 'show-hide-password' } onClick={() => setHideShow((prev) => !prev)}>{hideShow ? <VisibilityOff /> : <Visibility />}</span>
                </div>
              </div>

              <FormField inputType type={hideShow ? 'text' : 'password'} labelName="Confirm Password" name="confirmPassword" value={postData.confirmPassword} handleChange={handleChange} isLoadingBtn={isLoadingBtn.confirmPassword} isValid={isValid.confirmPassword} errors={confirmPasswordErrors || []} />

              <Button onClickButton buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} Creating Admin...</> : "Sign Up"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />
              
              {errorMessage && (
                <div className="form-response-message">
                <p className="error-msg">{errorMessage}</p>
                </div>
              )}
            </div>
          ) : (
            <VerifyOtp id={otpData?._id} emailAddress={otpData?.emailAddress} />
          )}

          <div className='have-account-wrapper'>
            <span>Already have an account?</span>
            <Link className='account-reg-log' to="/login">Sign in</Link>
          </div>
        </form>
      </div>
    </>
  )
}

export default AccountRegister